import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Heart, Shield, Search, X, RefreshCw } from 'lucide-react';
import { usePublicData } from '@/lib/usePublicData';
import { useTheme } from '@/lib/useTheme';
import { PageMeta } from '@/components/PageMeta';
import { AffiliateDisclaimer } from '@/components/AffiliateDisclaimer';
import { useAnalytics } from '@/lib/useAnalytics';
import { ScrollControls } from '@/components/redesign/ScrollControls';
import { StormBackground } from '@/components/redesign/StormBackground';
import { ZapEffects } from '@/components/redesign/ZapEffects';
import { RedesignTabs } from '@/components/redesign/RedesignTabs';
import { HeroHeader } from '@/components/redesign/HeroHeader';
import routeMeta from '@/data/route-meta.json';
import { curatedFallbackSections } from '@/components/redesign/catalogue';

const DESKTOP_QUERY = '(min-width: 1000px)';

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
};

/**
 * Local redesign draft: the iris header, then the tabs over glass stack cards.
 * Mobile: the "Current" storm sky. Desktop: the same sky in ice, plus Chorus lanes,
 * streams hopping along the stack, and the iris preview.
 */
const Redesign = ({ preview = false }: { preview?: boolean } = {}) => {
  const { config, sections: dbSections, loading: dbLoading, error, retry, loaded, fallbackAllowed } = usePublicData();
  const { trackClick } = useAnalytics(window.location.pathname, !preview);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  useTheme(config?.theme_colors);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const visible = true;

  const sections = !loaded && fallbackAllowed ? curatedFallbackSections : dbSections;
  const trackLink = useCallback((id: string) => {
    const link = sections.flatMap((section) => section.links).find((item) => item.id === id);
    trackClick(link?.title || id, id);
  }, [sections, trackClick]);
  const filteredSections = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    if (!term) return sections;
    return sections.map((section) => ({
      ...section,
      links: section.links.filter((link) =>
        [link.title, link.subtitle, link.description, section.title].some((text) => text.toLocaleLowerCase().includes(term))
      ),
    })).filter((section) => section.links.length > 0);
  }, [sections, query]);
  const resultCount = filteredSections.reduce((total, section) => total + section.links.length, 0);


  const welcome = config?.welcome_text || <>ברוכים הבאים לקהילה של{' '}
    <span lang="en" className="text-white/90 font-medium">nVision Digital AI</span>{' '}
    — תכנים מעניינים ורלוונטיים לכל מי שמתעניין ב
    <span className="text-white/90">בינה מלאכותית</span>,{' '}
    <span lang="en" className="text-white/90">Vibe Coding</span>,{' '}
    תכנות לאנשים שלא יודעים לתכנת, יצירת תמונות וסרטונים,
    דברים שלא תמצאו לרוב בשום מקום אחר, והכי חשוב —{' '}
    <span className="text-[#8FE3F0] font-medium">מעניין</span>.</>;

  return (
    <div dir="rtl" className="min-h-screen relative overflow-x-clip" style={{ background: '#060B1E' }}>
      <PageMeta
        title={config?.site_title ? `${config.site_title} — קהילת בינה מלאכותית` : routeMeta['/'].title}
        description={config?.site_description || routeMeta['/'].description}
        ogTitle={config?.og_title || undefined}
        ogDescription={config?.og_description || undefined}
        ogImageUrl={config?.og_image_url || undefined}
        canonicalUrl={config?.canonical_url || undefined}
        metaKeywords={config?.meta_keywords || undefined}
        noIndex={preview}
      />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:right-4 focus:z-[100] focus:rounded-lg focus:bg-primary focus:px-6 focus:py-3 focus:text-primary-foreground">דלג לתוכן הראשי</a>
      <ScrollControls />

      <StormBackground accent={isDesktop ? 'ice' : 'gold'} />
      <ZapEffects />

      <main
        id="main-content"
        tabIndex={-1}
        className={`relative z-10 w-full mx-auto px-4 sm:px-6 pt-10 md:pt-14 pb-16 ${isDesktop ? 'max-w-6xl' : 'max-w-5xl'}`}
      >
        <HeroHeader visible={visible} isDesktop={isDesktop} welcome={welcome} />
        <div className="mx-auto mb-6 max-w-3xl px-3 sm:px-4">
          <div className="flex items-center rounded-xl border border-white/10 bg-[#060B1E]/60 px-3 py-2 backdrop-blur-md">
            <Search className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
            <input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="חיפוש כלים וקהילות" placeholder="חיפוש כלים וקהילות..." className="min-w-0 flex-1 bg-transparent px-3 py-1 text-sm text-white placeholder:text-white/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
            {query && <button type="button" aria-label="נקה חיפוש" onClick={() => { setQuery(''); searchRef.current?.focus(); }} className="rounded-lg p-2 text-white/70 focus-visible:ring-2 focus-visible:ring-primary"><X className="h-4 w-4" aria-hidden="true" /></button>}
          </div>
          <p role="status" className="mt-2 text-center text-xs text-white/60">{query.trim() ? `${resultCount} תוצאות` : ''}</p>
          {error && <div role="status" className="mt-3 flex items-center justify-center gap-2 text-xs text-white/70"><span>{loaded ? 'העדכון מהשרת אינו זמין כרגע. מוצגים הנתונים האחרונים שהתקבלו.' : 'התוכן אינו זמין כרגע. נסו שוב.'}</span><button onClick={retry} type="button" className="flex items-center gap-1 rounded-lg border border-white/20 px-2 py-1"><RefreshCw className="h-3 w-3" aria-hidden="true" />נסה שוב</button></div>}
        </div>
        {filteredSections.length ? <RedesignTabs sections={filteredSections} visible={visible} withPreview={isDesktop} onLinkClick={trackLink} /> : !loaded && !fallbackAllowed ? (dbLoading ? <p role="status" className="py-12 text-center text-white/70">טוען את הקטלוג...</p> : null) : <p role="status" className="py-12 text-center text-white/70">{query.trim() ? 'לא נמצאו כלים מתאימים. נסו חיפוש אחר.' : 'אין כרגע קישורים להצגה.'}</p>}
        <AffiliateDisclaimer visible={visible} customText={config?.affiliate_disclaimer_text || undefined} />
      </main>

        <footer role="contentinfo" className="relative z-10 mx-auto mt-0 max-w-6xl px-4 pb-24 text-center sm:pb-8">
          <nav aria-label="ניווט תחתון" className="mb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/accessibility" className="text-sm text-white/60 underline underline-offset-2 hover:text-white/80">הצהרת נגישות</Link>
            <span className="text-white/40" aria-hidden="true">|</span>
            <Link to="/privacy" className="text-sm text-white/60 underline underline-offset-2 hover:text-white/80">מדיניות פרטיות</Link>
          </nav>
          <div className="flex items-center justify-center gap-2 text-sm text-white/60">
            <span lang="en">{config?.site_title || 'nVision Digital AI'}</span>
            <button type="button" onClick={() => navigate('/admin')} aria-label="ניהול" className="inline-flex">
              <Heart className="h-[18px] w-[18px] animate-pulse text-red-400/50" aria-hidden="true" />
            </button>
            <span>{new Date().getFullYear()}</span>
          </div>
          <p className="mt-3 flex items-center justify-center gap-1.5 px-4 text-xs text-white/60">
            <Shield className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>ספירת ביקורים וקליקים ללא עוגיות מעקב · פרטים במדיניות הפרטיות</span>
          </p>
        </footer>

    </div>
  );
};

export default Redesign;
